import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { Controller } from 'react-hook-form'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { selectProviders } from 'store/providers/slice'
import { getProviders } from 'store/providers/thunks'

interface Props {
  name: string
  control: any
  value?: number
}

export default function ProviderSelect({ name, control, value }: Props): JSX.Element {
  const [selected, setSelected] = useState<number | null>(value || null)
  const dispatch = useDispatch()
  const { error, status, providers } = useSelector(selectProviders)

  useEffect(() => {
    if (status === 'idle') {
      dispatch(getProviders())
    }
    if ((value || value === 0) && value !== selected) {
      setSelected(value)
    } else if (!value && value !== 0) {
      setSelected(null)
    }
  }, [status, dispatch, value, selected])

  const handleChange = (e: any) => {
    const newValue = e.target.value
    setSelected(newValue)
    return newValue
  }

  const getProviderOptions = () => {
    const filtered = providers.filter((p: Provider) => p.abbreviation !== 'TEST')
    const noProviderOption: Provider = {
      id: 0,
      abbreviation: 'No provider',
      name: 'No provider',
      options: null,
    }
    filtered.push(noProviderOption)
    return filtered
  }

  const getValue = (): string => {
    return selected !== null && selected >= 0 ? String(selected) : ''
  }

  return (
    <>
      <InputContainer margin="normal" fullWidth>
        <FieldLabel>
          <FormattedMessage id={'strings.provider'} />
        </FieldLabel>
        <Controller
          control={control}
          name={name}
          defaultValue={selected}
          render={({ field: { onChange } }) => (
            <Select
              onChange={(e) => onChange(handleChange(e))}
              error={error !== null}
              variant="outlined"
              displayEmpty
              value={getValue()}
            >
              {status === 'loading' && (
                <MenuItem value="">
                  <FormattedMessage id="strings.loading" />
                  ...
                </MenuItem>
              )}
              <ResetValue value="" disabled={getValue() === ''}>
                {getValue() !== '' ? <FormattedMessage id="strings.reset" /> : ''}
              </ResetValue>
              {getProviderOptions().map((provider: Provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.abbreviation}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </InputContainer>
    </>
  )
}

const InputContainer = styled(FormControl)`
  margin: 20px 0 0;
  && {
    & .MuiInputBase-root {
      padding: 0;
      height: 40px;
    }
    & .MuiSelect-root {
      padding: 5px 5px 5px 10px !important;
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
`

const FieldLabel = styled(InputLabel)`
  padding: 0 10px;
  margin-top: -20px;
  transition: none;
  transform: none;
`

const ResetValue = styled(MenuItem)`
  color: ${(props) => props.theme.customPalette.mediumGray};
`
